/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { Suspense, lazy, startTransition, useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { CookieProvider, useCookies } from './contexts/CookieContext';
import {
  SURFACES,
  armPageEngaged,
  disarmPageEngaged,
  initOutboundClickTracking,
  isTokenConfirmLoad,
  pageCategoryForPath,
  setAdConsent,
  setAnalyticsConsent,
  splitLanguageFromPath,
  trackEvent,
  trackPageview,
} from './lib/gtag';
import { Header } from './components/Header';
import { ScrollProgressBar } from './components/ScrollProgressBar';
import { FloatingCompareCta } from './components/FloatingCompareCta';
import { Footer } from './components/Footer';
import { ScrollToTop } from './components/ScrollToTop';
import { Hero } from './components/Hero';
import { ComparisonEngine } from './components/comparison/ComparisonEngine';
import { ComparisonSkeleton } from './components/comparison/ComparisonSkeleton';
import { InfoSection } from './components/InfoSection';
import { PartnerCTA } from './components/PartnerCTA';
import { CookieBanner } from './components/CookieBanner';
import { useLanguage } from './contexts/LanguageContext';
import { usePrerender } from './contexts/PrerenderContext';
import { cachedFetch, readCache } from './lib/apiCache';
import { sameJson } from './lib/sameJson';
import { usePageStructure } from './hooks/usePageStructure';
import { ContentBlock } from './components/ContentBlockRenderer';
import type { PageEntry } from './types/pageStructure';
import { isBlockEntry, isPresetEntry } from './types/pageStructure';

// Every page the bake writes is a lazy() since 2026-10-15, so the entry chunk
// carries the homepage (HomePage above: Hero + ComparisonEngine, the money
// page) and the chrome, and each other page arrives as its own chunk. The
// baked HTML stays complete: the bake renders with `prerender` from
// react-dom/static (src/entry-server.tsx), which waits for the chunk where
// `renderToString` gave up on the Suspense boundary below and shipped an empty
// <main> (the provider profile pages until 2026-08-25). The bake also writes a
// <link rel="modulepreload"> for the route's chunk from the Vite manifest
// (scripts/prerenderChunkPreload.mjs), and hydration keeps the baked HTML in
// place until the chunk is there. A NEW baked page needs its row in
// src/lib/routePageModules.js — routePageModules.test.ts fails without it.
// The client-only routes below (partner area, signup landings, /vergelijk)
// were lazy before and are never baked.
const AboutPage = lazy(() => import('./pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const HowWeComparePage = lazy(() =>
  import('./pages/HowWeComparePage').then((m) => ({ default: m.HowWeComparePage }))
);
const KennisbankPage = lazy(() =>
  import('./pages/KennisbankPage').then((m) => ({ default: m.KennisbankPage }))
);
const ArticlePage = lazy(() =>
  import('./pages/ArticlePage').then((m) => ({ default: m.ArticlePage }))
);
const PrivacyPage = lazy(() =>
  import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage }))
);
const TermsPage = lazy(() => import('./pages/TermsPage').then((m) => ({ default: m.TermsPage })));
const CookiePolicy = lazy(() =>
  import('./pages/CookiePolicy').then((m) => ({ default: m.CookiePolicy }))
);
const DisclaimerPage = lazy(() =>
  import('./pages/DisclaimerPage').then((m) => ({ default: m.DisclaimerPage }))
);
const ContactPage = lazy(() =>
  import('./pages/ContactPage').then((m) => ({ default: m.ContactPage }))
);
const PartnerPage = lazy(() =>
  import('./pages/PartnerPage').then((m) => ({ default: m.PartnerPage }))
);
const ZakelijkPage = lazy(() =>
  import('./pages/ZakelijkPage').then((m) => ({ default: m.ZakelijkPage }))
);
const GeenLaadpaalPage = lazy(() =>
  import('./pages/GeenLaadpaalPage').then((m) => ({ default: m.GeenLaadpaalPage }))
);
const AuthorPage = lazy(() =>
  import('./pages/AuthorPage').then((m) => ({ default: m.AuthorPage }))
);
const BegrippenlijstPage = lazy(() =>
  import('./pages/BegrippenlijstPage').then((m) => ({ default: m.BegrippenlijstPage }))
);
const BegripPage = lazy(() =>
  import('./pages/BegripPage').then((m) => ({ default: m.BegripPage }))
);
const NEAProvidersPage = lazy(() =>
  import('./pages/NEAProvidersPage').then((m) => ({ default: m.NEAProvidersPage }))
);
const MidCheckPage = lazy(() =>
  import('./pages/MidCheckPage').then((m) => ({ default: m.MidCheckPage }))
);
const ErePricePage = lazy(() =>
  import('./pages/ErePricePage').then((m) => ({ default: m.ErePricePage }))
);
const ChargerBrandsPage = lazy(() =>
  import('./pages/ChargerBrandsPage').then((m) => ({ default: m.ChargerBrandsPage }))
);
const ChargerBrandPage = lazy(() =>
  import('./pages/ChargerBrandPage').then((m) => ({ default: m.ChargerBrandPage }))
);
const ChargerModelPage = lazy(() =>
  import('./pages/ChargerModelPage').then((m) => ({ default: m.ChargerModelPage }))
);
const HeadToHeadPage = lazy(() =>
  import('./pages/HeadToHeadPage').then((m) => ({ default: m.HeadToHeadPage }))
);
const ProviderProfilePage = lazy(() =>
  import('./pages/ProviderProfilePage').then((m) => ({ default: m.ProviderProfilePage }))
);
const PartnerLoginPage = lazy(() =>
  import('./pages/PartnerLoginPage').then((m) => ({ default: m.PartnerLoginPage }))
);
const PartnerPortalPage = lazy(() =>
  import('./pages/PartnerPortalPage').then((m) => ({ default: m.PartnerPortalPage }))
);
const PartnerInviteAcceptPage = lazy(() =>
  import('./pages/PartnerInviteAcceptPage').then((m) => ({ default: m.PartnerInviteAcceptPage }))
);
const PartnerForgotPasswordPage = lazy(() =>
  import('./pages/PartnerForgotPasswordPage').then((m) => ({
    default: m.PartnerForgotPasswordPage,
  }))
);
const PartnerResetPasswordPage = lazy(() =>
  import('./pages/PartnerResetPasswordPage').then((m) => ({ default: m.PartnerResetPasswordPage }))
);
const PartnerContactRedirect: React.FC = () => {
  const { lang } = useParams();
  const target = `/${lang || 'nl'}/partner#partner-contact-form`;
  return <Navigate to={target} replace />;
};
// A former address of the overview or of a profile page (LEGACY_PROVIDERS_SEGMENTS
// in src/lib/providerRoutes.js) → its address under /providers, query and hash
// kept. The server answers these with a 301 before the app loads
// (server/spaRenderer.ts, a stale slug resolved in the same hop); this is what
// answers on the dev server, which has no redirects, and after an in-app
// navigation to an old link. /aanbieders/contact keeps its own route below.
const LegacyProvidersRedirect: React.FC = () => {
  const { lang, '*': rest } = useParams();
  const { search, hash } = useLocation();
  const slug = (rest ?? '').split('/')[0];
  const base = slug ? providerProfilePath(lang ?? 'nl', slug) : providerHubPath(lang ?? 'nl');
  return <Navigate to={`${base}${search}${hash}`} replace />;
};
// Client-only "compare up to 3" view (/:lang/vergelijk?ids=). NOT prerendered —
// the bake never renders the bare /vergelijk route, so this chunk is never
// loaded on the server. The 2-way /vergelijk/:matchup SEO pages are the
// HeadToHeadPage above, baked per matchup.
const MultiCompareView = lazy(() => import('./pages/MultiCompareView'));
// Double-opt-in landing for the signup form: linked directly from the confirm
// mail (id + token in the query; the page POSTs the confirmation), or reached
// via the legacy GET /api/leads/confirm redirect. Never prerendered → lazy
// keeps it out of the SSR graph.
const SignupConfirmPage = lazy(() => import('./pages/SignupConfirmPage'));
// Its twin for the overstapherinnering's one-click unsubscribe link (the page
// POSTs the unsubscribe). Never prerendered → lazy, like the confirm page.
const SignupUnsubscribePage = lazy(() => import('./pages/SignupUnsubscribePage'));

import { ADMIN_PATH } from './lib/adminPath';
import {
  LEGACY_PROVIDERS_SEGMENTS,
  providerHubPath,
  providerProfilePath,
} from './lib/providerRoutes.js';
import { type EREProvider } from './types';

import { Loader2, AlertCircle, Lock, User, Key } from 'lucide-react';
import { LazyMotion, domAnimation, m } from 'motion/react';
const AdminPage = lazy(() => import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })));
// Lazy like the pages that render it for an unknown slug (AuthorPage,
// BegripPage, ChargerBrandPage, ChargerModelPage, GeenLaadpaalPage import it statically): Rollup
// gives it a chunk those pages share, and the catch-all route loads that one.
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);
import { ErrorBoundary } from './components/ErrorBoundary';
import { useSEO } from './hooks/useSEO';
import { useFAQSchema } from './hooks/useFAQSchema';
import { useProviderListSchema } from './hooks/useProviderListSchema';

// Fallback ordering — used when /api/page-structure hasn't responded yet. Hero
// is always rendered separately, so this list is the moveable middle only.
const DEFAULT_HOMEPAGE_ORDER = ['comparison', 'info'];

/**
 * The homepage's info section with the FAQPage JSON-LD of its accordion: the node
 * exists exactly while the questions are on the page, so hiding the section in the
 * admin hides them from crawlers too.
 */
function InfoSectionWithFaqSchema() {
  useFAQSchema();
  return <InfoSection />;
}

export function HomePage({
  providers,
  loading,
  error,
}: {
  providers: EREProvider[];
  loading: boolean;
  error: string | null;
}) {
  const { t, language } = useLanguage();
  useSEO('home_title', 'home_description');
  useProviderListSchema(providers);

  // Seeded from the bake (or the cache), refetched after hydration without a
  // re-render when nothing changed; null = the legacy section order below.
  const pageStructure = usePageStructure();

  const entries: PageEntry[] | null = pageStructure?.pages?.homepage?.entries ?? null;

  // Preset registry: each entry of kind 'preset' looks itself up here. Unknown
  // preset ids render nothing (no-op) so we can carry historical preset entries
  // through the structure without crashing on a missing renderer.
  const PRESET_RENDERERS: Record<string, React.ReactNode> = {
    comparison: <ComparisonEngine key="comparison" providers={providers} />,
    info: <InfoSectionWithFaqSchema key="info" />,
  };

  // The hero is a preset entry in the page structure but is NOT looked up
  // through the registry: it renders exactly once, below, in the same tree
  // position for every state of this page. Each state used to render its own
  // <Hero> (a bare child of the skeleton fragment, a keyed Fragment inside the
  // entries walk once the data was in), so the moment the providers arrived
  // React unmounted and remounted it: the entrance animation replayed, the
  // filters card lost its state, and an estimator opened from the skeleton —
  // a common moment on a phone, where that fetch takes a while — closed
  // itself, with its `?estimator=` param already consumed.
  const renderPreset = (preset: string): React.ReactNode =>
    preset === 'hero' ? null : (PRESET_RENDERERS[preset] ?? null);

  let body: React.ReactNode;
  if (loading) {
    // Placeholders in the silhouette of the comparer's rows
    // (src/components/comparison/ComparisonSkeleton.tsx).
    body = <ComparisonSkeleton />;
  } else if (error) {
    body = (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <div className="bg-red-900/20 border border-red-800/50 p-6 rounded-3xl">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-red-300 mb-2">{t('app.error_title')}</h3>
          <p className="text-red-400">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 px-6 py-2 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all"
          >
            {t('app.retry')}
          </button>
        </div>
      </div>
    );
  } else if (entries) {
    // Walk the unified entries list. Hero stays locked-top by the page-
    // structure invariant; the moveable middle can contain freeform blocks.
    body = (
      <>
        {entries.map((entry) => {
          if (entry.hidden) return null;
          if (isPresetEntry(entry)) {
            return <React.Fragment key={entry.id}>{renderPreset(entry.preset)}</React.Fragment>;
          }
          if (isBlockEntry(entry)) {
            const block = entry.block[language];
            return (
              <section key={entry.id} className="py-16">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                  <ContentBlock block={block} />
                </div>
              </section>
            );
          }
          return null;
        })}
        <PartnerCTA />
      </>
    );
  } else {
    // Legacy fallback — used only briefly until the page-structure fetch
    // resolves. Reads `homepage_sections.order` to mirror the pre-Phase-2
    // HomePage so users never see a blank page during the round-trip.
    const rawOrder = t('homepage_sections.order', { returnObjects: true });
    const sectionOrder: string[] = Array.isArray(rawOrder)
      ? rawOrder.filter((id: string) => id !== 'hero')
      : DEFAULT_HOMEPAGE_ORDER;
    body = (
      <>
        {sectionOrder.map((id) => (
          <React.Fragment key={id}>{renderPreset(id)}</React.Fragment>
        ))}
        <PartnerCTA />
      </>
    );
  }

  return (
    <>
      <Hero providerCount={new Set(providers.map((p) => p.name)).size} providers={providers} />
      {body}
    </>
  );
}

function LoginView({ onLogin }: { onLogin: () => void }) {
  const { t } = useLanguage();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });
      if (res.ok) {
        onLogin();
      } else {
        // Read the body as text first so a non-JSON error response (e.g. an
        // HTML 502 from a proxy) doesn't crash res.json() and disappear into
        // the generic connection_failed branch.
        const text = await res.text();
        let message = '';
        try {
          message = JSON.parse(text)?.error ?? '';
        } catch {
          /* not JSON — fall through to default */
        }
        setError(message || t('auth.invalid_credentials'));
      }
    } catch (_err) {
      setError(t('auth.connection_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <m.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl p-10 shadow-2xl relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-1.5 bg-emerald-600" />
        <div className="flex flex-col items-center mb-10">
          <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center mb-6 shadow-xl shadow-emerald-900/40">
            <Lock className="w-8 h-8 text-white" />
          </div>
          {/* Dutch-only admin: the card used to be English with a Dutch error
              underneath it, which read as a glitch on every mistyped password. */}
          <h2 className="text-3xl font-black text-white tracking-tight">Beheerderslogin</h2>
          <p className="text-fg-muted font-bold mt-2 uppercase tracking-widest text-[10px]">
            Beveiligd inloggen
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label
              htmlFor="admin-login-username"
              className="text-xs font-black text-fg-muted uppercase tracking-widest px-1 flex items-center gap-2"
            >
              <User className="w-3 h-3" /> Gebruikersnaam
            </label>
            <input
              id="admin-login-username"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-6 py-4 text-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none transition-all font-bold"
              placeholder="Gebruikersnaam"
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="admin-login-password"
              className="text-xs font-black text-fg-muted uppercase tracking-widest px-1 flex items-center gap-2"
            >
              <Key className="w-3 h-3" /> Wachtwoord
            </label>
            <input
              id="admin-login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-6 py-4 text-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none transition-all font-bold"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <p role="alert" className="text-red-400 text-sm font-bold text-center px-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-5 bg-emerald-700 hover:bg-emerald-800 text-white font-black rounded-2xl transition-all shadow-xl shadow-emerald-900/30 flex items-center justify-center disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : 'Inloggen'}
          </button>
        </form>
      </m.div>
    </div>
  );
}

function AdminRoute() {
  const [authed, setAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    fetch('/api/admin/me', { credentials: 'include' })
      .then((r) => setAuthed(r.ok))
      .catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (!authed) return;

    let timer: NodeJS.Timeout;
    // Server uses sliding-session JWT (refreshed on every authed request), so
    // an active admin stays logged in indefinitely. This timer is a last-resort
    // walk-away guard for sessions with zero user input — kept generous so a
    // long edit/read session doesn't trip a surprise force-reload.
    const INACTIVITY_LIMIT = 8 * 60 * 60 * 1000;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        fetch('/api/admin/logout', { method: 'POST', credentials: 'include' }).finally(() => {
          window.location.reload();
        });
      }, INACTIVITY_LIMIT);
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach((ev) => document.addEventListener(ev, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(timer);
      events.forEach((ev) => document.removeEventListener(ev, resetTimer));
    };
  }, [authed]);

  if (authed === null)
    return <Loader2 className="w-6 h-6 animate-spin m-auto mt-32 text-emerald-500" />;

  if (!authed) {
    return <LoginView onLogin={() => setAuthed(true)} />;
  }

  return (
    <Suspense fallback={<Loader2 className="w-6 h-6 animate-spin m-auto mt-32 text-emerald-500" />}>
      <AdminPage />
    </Suspense>
  );
}

function fireKeyPageView(pathname: string): void {
  const { language, path } = splitLanguageFromPath(pathname);
  const category = pageCategoryForPath(path);
  // Close the page being left first: its `page_leave` goes on the stream
  // ahead of this page's key_page_view, and no pending engagement marker from
  // it may fire against this path. A redirect, admin or 404 stops there — no
  // pageview, and no visit of its own.
  disarmPageEngaged();
  if (!category) return;

  trackEvent('key_page_view', {
    page_category: category,
    page_path: path,
    language,
  });
  // First-party bounce signal (`page_engaged` after 10 s in the foreground)
  // and the visit `page_leave` reports on (foreground time, scroll depth).
  armPageEngaged();
}

function AnalyticsTracker() {
  const { preferences, hasConsent } = useCookies();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith(ADMIN_PATH);
  const initialPageviewSkipped = useRef(false);

  useEffect(() => {
    // Only push consent once a REAL decision exists (stored or just made).
    // Pushing React's initial denied state would override the inline
    // snippet's region-scoped defaults and re-deny every undecided non-EEA
    // visitor the granted catch-all exists for (server/config.ts).
    if (!hasConsent) return;
    setAnalyticsConsent(preferences.analytical);
    setAdConsent(preferences.marketing);
  }, [hasConsent, preferences.analytical, preferences.marketing]);

  // One document-level listener catches every external-link click, including
  // links inside CMS rich text that can't carry their own onClick handlers.
  // Admin pages are excluded inside trackEvent itself.
  useEffect(() => initOutboundClickTracking(), []);

  useEffect(() => {
    if (isAdmin) {
      // Into the admin: the public page's visit ends here, unsent (the admin
      // emits nothing), so admin time can never count toward its page_leave.
      disarmPageEngaged();
      return;
    }
    // The confirm landing's token-bearing load reloads itself as ?status=…
    // right after its POST; that reload is the page view, so this load sends
    // none (isTokenConfirmLoad) — once per confirm, and never the token.
    const tokenLoad = isTokenConfirmLoad(location.pathname, location.search);
    // The inline gtag('config', ID) in index.html fires a page_view for the
    // initial load. Skip the first effect run so we don't double-count it.
    if (!initialPageviewSkipped.current) {
      initialPageviewSkipped.current = true;
      if (!tokenLoad) fireKeyPageView(location.pathname);
      return;
    }
    if (tokenLoad) return;
    trackPageview(location.pathname + location.search);
    fireKeyPageView(location.pathname);
    // Deliberately NOT keyed on location.search: filters write their state
    // via setSearchParams, and a query-only change is a filter interaction
    // (tracked as filter_change), not a pageview — keying on search inflated
    // homepage pageviews by one per filter click and re-armed page_engaged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, location.pathname]);

  return null;
}

/**
 * The two drifting background orbs, desktop only. The gate is CSS alone:
 * `hidden md:contents` keeps the two 1000 px blur(120px) layers — the most
 * expensive paint on the page — off a phone before any JS has run, and a CSS
 * animation inside a `display: none` subtree does not run. Until 2026-10-22
 * this also held `useIsMobile`, because the drift was a motion/react loop
 * that would otherwise have ticked on a phone; the drift is a CSS animation
 * now (the loop keyframes in src/index.css say why).
 */
function BackgroundOrbs() {
  return (
    <div className="hidden md:contents">
      <div className="absolute top-0 left-1/4 w-[1000px] h-[1000px] bg-emerald-900/20 rounded-full blur-[120px] -translate-y-1/2 opacity-30 motion-safe:animate-orb-drift-a" />
      <div className="absolute bottom-0 right-1/4 w-[1000px] h-[1000px] bg-blue-900/20 rounded-full blur-[120px] translate-y-1/2 opacity-20 motion-safe:animate-orb-drift-b" />
    </div>
  );
}

function MainApp() {
  const { t, language } = useLanguage();
  const prerender = usePrerender();
  // Seed from prerender → cached fetch → empty. Show the spinner only when
  // we genuinely have nothing yet. Refetch happens silently in the effect.
  const seededProviders =
    prerender?.providers && prerender.providers.length
      ? prerender.providers
      : (readCache<{ data: EREProvider[] }>('/api/providers')?.data ?? null);
  const [providers, setProviders] = useState<EREProvider[]>(() => seededProviders ?? []);
  const [loading, setLoading] = useState<boolean>(() => !seededProviders);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    cachedFetch<{ data?: EREProvider[]; error?: unknown }>('/api/providers')
      .then((result) => {
        if (cancelled) return;
        if (result.error && !Array.isArray(result.data)) {
          const errorMsg =
            typeof result.error === 'object' ? JSON.stringify(result.error) : String(result.error);
          throw new Error(errorMsg);
        }
        if (result.data) {
          if (Array.isArray(result.data) && result.data.length === 0) {
            // Don't clobber a previously-seeded list with an empty response;
            // surface error only when we have nothing to show.
            if (!seededProviders) {
              startTransition(() => setError(t('app.error_message')));
              // A visitor is looking at an empty money page — make revenue-
              // surface outages visible in the first-party stream in minutes,
              // instead of via a rank-correlated dip days later.
              trackEvent('providers_load_error', {
                surface: SURFACES.comparisonTable,
                language,
              });
            }
          } else if (seededProviders) {
            // Rows are already on screen: a refetch that matches them keeps
            // the seeded array (sameJson — no render), and a stale bake's
            // correction lands as a transition, never blocking a tap.
            const fresh = result.data;
            startTransition(() => setProviders((prev) => (sameJson(prev, fresh) ? prev : fresh)));
          } else {
            // A transition as well (2026-10-15): on a baked page that seeds
            // no providers this answer is the first update above the route's
            // Suspense boundary, and a plain update that reaches that boundary
            // while its lazy page chunk is still loading makes React client-
            // render the route (React #421: the baked <main> empties until the
            // chunk is in). A transition waits for the chunk instead —
            // docs/reference-ssr-prerendering.md, "Hydration keeps the baked
            // HTML". The SPA shell's skeleton → rows swap is not time-critical.
            const rows = result.data;
            startTransition(() => setProviders(rows));
          }
        }
        startTransition(() => setLoading(false));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Fetch error:', err);
        if (!seededProviders) {
          startTransition(() => setError(t('app.error_message')));
          trackEvent('providers_load_error', { surface: SURFACES.comparisonTable, language });
        }
        startTransition(() => setLoading(false));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col font-sans selection:bg-emerald-900 selection:text-emerald-100 relative transition-colors duration-300">
      <ScrollToTop />
      <AnalyticsTracker />

      {/* Global Background Elements — Moved to very back z-[-1] */}
      <div className="fixed inset-0 pointer-events-none z-[-1] overflow-hidden">
        <div className="absolute inset-0 bg-dot-pattern opacity-[0.15]" />
        <div className="absolute inset-0 bg-noise opacity-[0.02] mix-blend-overlay" />
        <BackgroundOrbs />
      </div>

      <div className="relative z-10 flex flex-col flex-grow">
        <Routes>
          <Route
            path={`${ADMIN_PATH}/*`}
            element={
              <ErrorBoundary>
                <AdminRoute />
              </ErrorBoundary>
            }
          />
          <Route
            path="/:lang/*"
            element={
              <>
                <Header />
                <ScrollProgressBar />
                {/* Both fixed overlays sit behind their own boundary, and both
                    render nothing when they fail. They are the two subtrees on
                    the page that third-party software reaches into and detaches
                    — consent blockers and page translation for the banner, ad
                    blockers for a bottom-right CTA card. React then cannot
                    unmount what it inserted ("Failed to execute 'removeChild'
                    on 'Node'", prd 2026-09-02/04/06, always the cookie banner,
                    always a kennisbank page). That failure happens in the
                    commit phase, so it walks up to the nearest boundary — which
                    was the root one, wrapping the article a visitor was reading
                    in a full-page crash card because an optional overlay could
                    not be removed. The overlays are not worth a page: the
                    consent decision is persisted before the banner unmounts,
                    and the CTA is a shortcut to a link the footer also has. */}
                <ErrorBoundary fallback={null} label="floating-compare-cta">
                  <FloatingCompareCta />
                </ErrorBoundary>
                <main className="flex-grow">
                  <RouteErrorBoundary>
                    <Suspense fallback={null}>
                      <Routes>
                        <Route
                          path="/"
                          element={
                            <HomePage providers={providers} loading={loading} error={error} />
                          }
                        />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/hoe-wij-vergelijken" element={<HowWeComparePage />} />
                        <Route path="/kennisbank" element={<KennisbankPage />} />
                        <Route path="/kennisbank/:slug" element={<ArticlePage />} />
                        <Route path="/begrippen" element={<BegrippenlijstPage />} />
                        <Route path="/begrippen/:slug" element={<BegripPage />} />
                        <Route path="/faq" element={<Navigate to="kennisbank" replace />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/cookies" element={<CookiePolicy />} />
                        <Route path="/disclaimer" element={<DisclaimerPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/zakelijk" element={<ZakelijkPage />} />
                        <Route path="/geen-laadpaal" element={<GeenLaadpaalPage />} />
                        <Route path="/geen-laadpaal/:provincie" element={<GeenLaadpaalPage />} />
                        <Route path="/auteur/:slug" element={<AuthorPage />} />
                        <Route path="/partner" element={<PartnerPage />} />
                        <Route path="/partner-login" element={<PartnerLoginPage />} />
                        <Route path="/partner-portal" element={<PartnerPortalPage />} />
                        <Route
                          path="/partner-invite/:token"
                          element={<PartnerInviteAcceptPage />}
                        />
                        <Route path="/partner-forgot" element={<PartnerForgotPasswordPage />} />
                        <Route
                          path="/partner-reset/:token"
                          element={<PartnerResetPasswordPage />}
                        />
                        <Route path="/aanbieders/contact" element={<PartnerContactRedirect />} />
                        {LEGACY_PROVIDERS_SEGMENTS.map((segment) => (
                          <Route
                            key={segment}
                            path={`/${segment}/*`}
                            element={<LegacyProvidersRedirect />}
                          />
                        ))}
                        <Route path="/providers" element={<NEAProvidersPage />} />
                        <Route path="/providers/:slug" element={<ProviderProfilePage />} />
                        <Route path="/vergelijk" element={<MultiCompareView />} />
                        <Route path="/vergelijk/:matchup" element={<HeadToHeadPage />} />
                        <Route path="/aanmelden/bevestigd" element={<SignupConfirmPage />} />
                        <Route path="/aanmelden/afgemeld" element={<SignupUnsubscribePage />} />
                        <Route path="/mid-check" element={<MidCheckPage />} />
                        <Route path="/laadpalen" element={<ChargerBrandsPage />} />
                        <Route path="/laadpalen/:slug" element={<ChargerBrandPage />} />
                        <Route path="/laadpalen/:slug/:model" element={<ChargerModelPage />} />
                        <Route path="/ere-prijs" element={<ErePricePage />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    </Suspense>
                  </RouteErrorBoundary>
                </main>
                <Footer />
                <ErrorBoundary fallback={null} label="cookie-banner">
                  <CookieBanner />
                </ErrorBoundary>
              </>
            }
          />
          {/* Bare-`/` (and any non-prefixed path) → redirect to /nl with the
              same suffix. Deterministic on server + client; user's last
              language is reapplied via setLanguage after they navigate. */}
          <Route path="*" element={<RootLanguageRedirect />} />
        </Routes>
      </div>
    </div>
  );
}

/**
 * Per-route crash isolation. The root boundary in App() is the last resort and
 * takes the entire page down with it; this one sits inside the layout, so a
 * crash in one route keeps header, footer and navigation usable. Keyed on the
 * pathname, so navigating away clears the error instead of leaving the visitor
 * stranded until they hard-reload.
 *
 * Mounted OUTSIDE <Suspense> on purpose: a lazily-imported route whose chunk
 * 404s after a deploy throws during the lazy import, and only a boundary above
 * Suspense sees it.
 */
function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary variant="inline" resetKey={location.pathname}>
      {children}
    </ErrorBoundary>
  );
}

function RootLanguageRedirect() {
  const location = useLocation();
  if (location.pathname.startsWith(ADMIN_PATH)) {
    return null;
  }
  let stored: string | null = null;
  try {
    stored = typeof window !== 'undefined' ? window.localStorage.getItem('language') : null;
  } catch {
    /* ignore (private browsing, etc.) */
  }
  const browserLang = typeof navigator !== 'undefined' ? navigator.language || '' : '';
  const target =
    stored === 'nl' || stored === 'en' ? stored : /^nl/i.test(browserLang) ? 'nl' : 'en';
  return <Navigate to={`/${target}${location.pathname}${location.search}`} replace />;
}

export default function App() {
  return (
    <ThemeProvider>
      <CookieProvider>
        <ErrorBoundary>
          <LanguageProvider>
            {/* The app's LazyMotion. Every animated element is an `m.*`, never a
                `motion.*`: `strict` throws on a `motion.*` in dev and
                motionImports.test.ts fails on it in CI (the full component set
                would put the whole library back in the preloaded motion chunk).
                domAnimation covers animate, exit, hover, tap, focus and inView;
                `layout` and drag need domMax, which AdminPage adds for its own
                subtree only — a public page must not use them. */}
            <LazyMotion features={domAnimation} strict>
              <MainApp />
            </LazyMotion>
          </LanguageProvider>
        </ErrorBoundary>
      </CookieProvider>
    </ThemeProvider>
  );
}
